import React, { useState } from 'react';
import { Delete, Edit } from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { JobCode, deleteJobCode } from '../../../../core/API/guestConnect';
import styles from '../SchedulerSetting.module.scss';

interface RewardsListProps {
	rewards: JobCode[];
	onRewardCreated: () => void;
	onEditClick: (reward: JobCode) => void;
}

const RewardsList: React.FC<RewardsListProps> = ({ rewards, onRewardCreated, onEditClick }) => {
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [rewardToDelete, setRewardToDelete] = useState<JobCode | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const handleDeleteClick = (reward: JobCode) => {
		if (rewards.length <= 1) return;
		setRewardToDelete(reward);
		setDeleteDialogOpen(true);
	};

	const handleCancelDelete = () => {
		setDeleteDialogOpen(false);
		setRewardToDelete(null);
	};

	const handleConfirmDelete = async () => {
		if (!rewardToDelete) return;
		
		setIsDeleting(true);
		try {
			await deleteJobCode(Number((rewardToDelete as any).id));
			onRewardCreated();
			setDeleteDialogOpen(false);
			setRewardToDelete(null);
		} catch (e) {
			console.error('Failed to delete reward:', e);
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<>
			<div className={styles.tableContainer}>
				<table className={styles.workplaceTable}>
					<thead>
						<tr>
							<th>Reward Offered</th>
							<th>Reward Summary to Display on Campaign</th>
							<th style={{ width: 48, textAlign: 'center' }}>Edit</th>
							<th style={{ width: 48, textAlign: 'center' }}>Delete</th>
						</tr>
					</thead>
					<tbody>
						{[...rewards].map((reward) => (
							<tr key={reward.id}>
								<td>
									<span>{reward.title}</span>
								</td>
								<td>
									<span>{reward.description || '-'}</span>
								</td>
								<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
									<button
										className={styles.iconButton}
										title="Edit"
										onClick={() => onEditClick(reward)}
									>
										<Edit className={styles.editIcon} />
									</button>
								</td>
								<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
									<button
										onClick={() => handleDeleteClick(reward)}
										title="Delete"
										className={styles.iconButton}
									>
										<Delete className={styles.deleteIcon} />
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={deleteDialogOpen}
				onClose={handleCancelDelete}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
				maxWidth="sm"
				fullWidth
				style={{ zIndex: 9999 }}
				PaperProps={{
					style: {
						backgroundColor: 'white',
						boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
						borderRadius: '8px',
						minWidth: '400px'
					}
				}}
			>
				<DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you want to delete the reward{' '}
						{rewardToDelete && <strong>&ldquo;{rewardToDelete.title}&rdquo;</strong>}? This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleCancelDelete}
						style={{ color: 'black' }}
						disabled={isDeleting}
					>
						Cancel
					</Button>
					<Button
						style={{ backgroundColor: 'red', color: 'white' }}
						onClick={handleConfirmDelete}
						disabled={isDeleting}
					>
						{isDeleting ? 'Deleting...' : 'Delete'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default RewardsList;
